import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTheme } from '@/app/ThemeContext';
import {
  useDeleteMasterKey,
  useMasterKey,
  useSetMasterKey,
} from '@/sections/customerManagement/components/masterDashboard/hooks/useMasterKey';
import type { PluginStatus } from '@/sections/customerManagement/components/masterDashboard/hooks/useEnrichmentPlugins';

interface PluginStatusCardProps {
  plugin: PluginStatus;
}

export const PluginStatusCard: React.FC<PluginStatusCardProps> = ({
  plugin,
}) => {
  const { currentTheme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const { data: keyInfo } = useMasterKey(plugin.plugin, expanded);
  const { mutateAsync: setMasterKey, isPending: isSaving } = useSetMasterKey();
  const { mutateAsync: deleteMasterKey, isPending: isDeleting } =
    useDeleteMasterKey();
  const isPending = isSaving || isDeleting;

  const updateField = (name: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [name]: value }));
  };

  const toggleVisibility = (name: string) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    const apiKeyField = plugin.credentialFields.find(
      f => f.name === 'api_key'
    );
    const apiKeyValue = fieldValues['api_key']?.trim() ?? '';
    if (apiKeyField && !apiKeyValue) return;

    const extra: Record<string, string> = {};
    for (const field of plugin.credentialFields) {
      if (field.name === 'api_key') continue;
      const val = fieldValues[field.name]?.trim();
      if (val) extra[field.name] = val;
    }

    try {
      await setMasterKey({
        plugin: plugin.plugin,
        apiKey: apiKeyValue,
        extra: Object.keys(extra).length > 0 ? extra : undefined,
      });
      setFieldValues({});
    } catch {
      // Mutation hook surfaces the error via toast/defaultErrorMessage.
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMasterKey({ plugin: plugin.plugin });
      setFieldValues({});
    } catch {
      // Mutation hook surfaces the error via toast/defaultErrorMessage.
    }
  };

  const hasRequiredValues = plugin.credentialFields
    .filter(f => f.required)
    .every(f => (fieldValues[f.name]?.trim() ?? '').length > 0);

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary flex flex-col rounded-lg border">
      <button
        type="button"
        className="flex w-full flex-col gap-2 p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={`/icons/${currentTheme}/${plugin.plugin.replace('pius_', '')}.svg`}
              alt={plugin.name}
              className="size-5 rounded"
            />
            <h3 className="text-chariot-text-primary text-sm font-medium">
              {plugin.name}
            </h3>
          </div>
          {plugin.hasMasterKey ? (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <CheckCircleIcon className="size-4" />
              Active
            </span>
          ) : (
            <span className="text-chariot-text-tertiary flex items-center gap-1 text-xs">
              <XCircleIcon className="size-4" />
              No master key
            </span>
          )}
        </div>
        <p className="text-chariot-text-secondary text-xs">
          {plugin.description}
        </p>
      </button>

      <div
        className={twMerge(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          expanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div className="border-chariot-stroke flex flex-col gap-3 border-t px-4 py-3">
            <div className="flex items-center gap-2">
              <KeyIcon className="text-chariot-text-tertiary size-4 shrink-0" />
              <span className="text-chariot-text-secondary text-xs">
                {keyInfo?.configured
                  ? `Current key: ${keyInfo.maskedKey}`
                  : 'No master API key configured'}
              </span>
            </div>

            {plugin.credentialFields.map(field => (
              <div key={field.name} className="flex flex-col gap-1">
                <span className="text-chariot-text-secondary text-xs font-medium">
                  {field.label}
                  {field.required && (
                    <span className="text-red-400"> *</span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type={
                        field.isPassword && !visibleFields.has(field.name)
                          ? 'password'
                          : 'text'
                      }
                      placeholder={field.placeholder}
                      value={fieldValues[field.name] ?? ''}
                      onChange={e => updateField(field.name, e.target.value)}
                      className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary placeholder:text-chariot-text-tertiary w-full rounded border px-3 py-1.5 pr-8 text-xs outline-none"
                    />
                    {field.isPassword && (
                      <button
                        type="button"
                        onClick={() => toggleVisibility(field.name)}
                        className="text-chariot-text-tertiary hover:text-chariot-text-secondary absolute top-1/2 right-2 -translate-y-1/2"
                      >
                        {visibleFields.has(field.name) ? (
                          <EyeSlashIcon className="size-3.5" />
                        ) : (
                          <EyeIcon className="size-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-2">
              {keyInfo?.configured && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isPending}
                  className="shrink-0 rounded px-3 py-1.5 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                >
                  {isDeleting ? 'Removing...' : 'Clear Key'}
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={isPending || !hasRequiredValues}
                className="shrink-0 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
